import type { ReactNode } from "react";

interface CardProps {
  children: ReactNode;
  onClick?: () => void;
  "data-testid"?: string;
  className?: string;
}

/**
 * Base Card component that can optionally be made clickable.
 * When onClick is provided, the entire card becomes an interactive element
 * with keyboard accessibility and hover states.
 */
export function Card({ children, onClick, "data-testid": testId, className }: CardProps) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onClick();
    }
  };

  const baseClasses =
    "relative flex flex-col gap-4 rounded-xl border border-theme-primary bg-white p-4 shadow-sm";
  const interactiveClasses = onClick
    ? "cursor-pointer hover:border-hover hover:bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary"
    : "";

  return (
    <div
      className={`${baseClasses} ${interactiveClasses} ${className ?? ""}`}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? "button" : undefined}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

interface CardHeaderProps {
  children: ReactNode;
}

/**
 * Card.Header wraps title, subtitle, and badge elements.
 * Note: Card.TopRight is positioned absolutely and doesn't participate in this layout.
 */
function CardHeader({ children }: CardHeaderProps) {
  return <div className="flex flex-wrap items-center gap-2">{children}</div>;
}

Card.Header = CardHeader;

interface CardTitleProps {
  children: ReactNode;
}

/**
 * Card.Title displays the primary heading for the card.
 * Text is truncated with ellipsis if it overflows.
 * Right padding on sm+ screens avoids overlap with Card.TopRight content.
 */
function CardTitle({ children }: CardTitleProps) {
  return <h3 className="text-lg font-semibold text-brand-950 truncate sm:pr-10">{children}</h3>;
}

Card.Title = CardTitle;

interface CardSubtitleProps {
  children: ReactNode;
  className?: string;
}

/**
 * Card.Subtitle displays secondary text with muted styling.
 * This replaces both CardDescription and CardMeta from the old system.
 */
function CardSubtitle({ children, className }: CardSubtitleProps) {
  return <p className={`text-sm text-theme-secondary ${className ?? ""}`}>{children}</p>;
}

Card.Subtitle = CardSubtitle;

interface CardBadgeProps {
  children: ReactNode;
}

/**
 * Card.Badge is a wrapper for Badge components, no additional styling.
 */
function CardBadge({ children }: CardBadgeProps) {
  return <div>{children}</div>;
}

Card.Badge = CardBadge;

interface CardTopRightProps {
  children: ReactNode;
  centered?: boolean;
  /**
   * When true, the content appears first on mobile (above other card content)
   * and switches to absolute positioning on sm+ screens.
   * Use this when the content (like a status badge) is too wide to fit
   * alongside the title on narrow screens.
   */
  stackOnMobile?: boolean;
}

/**
 * Card.TopRight positions content absolutely in the top-right corner.
 * By default positioned at the top, but can be vertically centered with the centered prop.
 * Automatically prevents click events from propagating to the Card's onClick handler.
 *
 * When stackOnMobile is true, content flows normally on mobile and only becomes
 * absolutely positioned on sm+ screens to prevent overlap with titles.
 */
function CardTopRight({ children, centered = false, stackOnMobile = false }: CardTopRightProps) {
  if (stackOnMobile) {
    // On mobile: appears first (order-first) for visibility
    // On sm+: absolutely positioned in top-right
    const smPositionClasses = centered
      ? "sm:absolute sm:right-4 sm:top-1/2 sm:-translate-y-1/2"
      : "sm:absolute sm:right-4 sm:top-4";

    return (
      <div
        className={`order-first sm:order-none ${smPositionClasses}`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    );
  }

  const positionClasses = centered
    ? "absolute right-4 top-1/2 -translate-y-1/2"
    : "absolute right-4 top-4";

  return (
    <div
      className={positionClasses}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
}

Card.TopRight = CardTopRight;

interface CardContentProps {
  children: ReactNode;
  className?: string;
}

/**
 * Card.Content is the main flexible content area of the card.
 */
function CardContent({ children, className }: CardContentProps) {
  return <div className={`flex-1 ${className ?? ""}`}>{children}</div>;
}

Card.Content = CardContent;

interface CardActionsProps {
  children: ReactNode;
}

/**
 * Card.Actions provides a bottom action area for buttons.
 * On mobile, buttons stack vertically for better touch targets.
 */
function CardActions({ children }: CardActionsProps) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">{children}</div>
  );
}

Card.Actions = CardActions;

interface CardBottomRightProps {
  children: ReactNode;
}

/**
 * Card.BottomRight positions content in the bottom area, right-aligned.
 * Uses flex layout instead of absolute positioning to avoid overlap.
 * Automatically prevents click events from propagating to the Card's onClick handler.
 */
function CardBottomRight({ children }: CardBottomRightProps) {
  return (
    <div
      className="flex justify-end"
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
}

Card.BottomRight = CardBottomRight;
